import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useActiveRegions } from '../hooks/useProjects';

const Footer = () => {
  const { t } = useTranslation();
  const { labels } = useActiveRegions();
  
  return (
    <footer className="bg-[#102131] text-[#F7FDFF] border-t border-[#CDA259]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <img
              src="https://customer-assets.emergentagent.com/job_1d14a01e-caf6-4089-b4cd-70016bee0437/artifacts/l0vzx91y_FundoraSimbolisSviesiVersija.png"
              alt="Fundora Logo"
              className="h-12 w-auto"
            />
            <p className="text-sm text-[#F7FDFF]/70">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#CDA259]">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/properties" className="text-sm text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors">
                  {t('nav.cases')}
                </Link>
              </li>
              <li>
                <Link to="/about-us" className="text-sm text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors">
                  {t('nav.faq')}
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-sm text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors">
                  {t('privacy.title')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Regions */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#CDA259]">{t('footer.ourRegions')}</h3>
            <ul className="space-y-2">
              {[
                { value: 'Northern Cyprus', key: 'northernCyprus' },
                { value: 'Bali (Indonesia)', key: 'bali' },
                { value: 'Vietnam', key: 'vietnam' },
                { value: 'Maldives', key: 'maldives' }
              ]
                .filter((region) => labels.includes(region.value))
                .map((region) => (
                  <li key={region.key}>
                    <Link
                      to={`/properties?region=${encodeURIComponent(region.value)}`}
                      data-testid={`footer-region-${region.key}`}
                      className="text-sm text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors"
                    >
                      {t(`regions.${region.key}.name`)}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#CDA259]">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2 text-sm text-[#F7FDFF]/70">
                <Mail size={16} className="text-[#CDA259]" />
                <span>info@fundoraglobal.com</span>
              </li>
              <li className="flex items-center space-x-2 text-sm text-[#F7FDFF]/70">
                <Phone size={16} className="text-[#CDA259]" />
                <span>+370 602 22526</span>
              </li>
              <li className="flex items-center space-x-2 text-sm text-[#F7FDFF]/70">
                <MapPin size={16} className="text-[#CDA259]" />
                <span>Žalgirio g. 93, Vilnius, Lithuania</span>
              </li>
            </ul>
            <div className="flex space-x-4 mt-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61576201395741" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://www.instagram.com/fundorainvestments/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#F7FDFF]/70 hover:text-[#CDA259] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-[#CDA259]/20 mt-8 pt-8 text-center text-sm text-[#F7FDFF]/70">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
